/**
 * OAuth API Client
 * Functions for Meta Ads and Google/BigQuery OAuth flows
 */

const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL;
const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const API_BASE = PUBLIC_API_URL
  ? PUBLIC_API_URL
  : PUBLIC_IS_PROD === 'TRUE'
  ? 'https://app.humblytics.com'
  : PUBLIC_IS_LOCAL === 'TRUE'
  ? 'http://localhost:3000'
  : 'https://humblytics.onrender.com';

/**
 * Build common request headers with authentication
 */
function authHeaders(token: string, contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  return headers;
}

interface OAuthInitiateResponse {
  authUrl: string;
  state: string;
}

export interface MetaConnection {
  id: string;
  propertyId: string;
  userId?: string;
  metaUserId: string;
  metaUserEmail?: string;
  metaUserName?: string;
  adAccounts?: Array<{
    id: string;
    name: string;
    currency: string;
    timezone: string;
    isActive: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  tokenExpiresAt?: string;
  syncState?: {
    lastSyncDate?: string;
    status: 'idle' | 'syncing' | 'error';
    errorMessage?: string;
  };
}

export interface GoogleConnection {
  id: string;
  propertyId: string;
  userId?: string;
  googleUserId: string;
  googleUserEmail?: string;
  googleUserName?: string;
  ga4Accounts?: Array<{
    id: string;
    name: string;
    createTime?: string;
    updateTime?: string;
    isActive: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  tokenExpiresAt?: string;
  syncState?: {
    lastSyncDate?: string;
    status: 'idle' | 'syncing' | 'error';
    errorMessage?: string;
  };
}

interface ConnectionStatus {
  status: 'connected' | 'expired' | 'error';
  canRefresh: boolean;
  message?: string;
  tokenExpiresAt?: string;
  syncState?: {
    lastSyncDate?: string;
    status: 'idle' | 'syncing' | 'error';
    errorMessage?: string;
  };
}

/**
 * Initiate Meta Ads OAuth flow
 */
export async function initiateMetaOAuth(propertyId: string, token: string, userId?: string): Promise<OAuthInitiateResponse> {
  const params = new URLSearchParams({ propertyId });
  if (userId) {
    params.append('userId', userId);
  }

  const response = await fetch(`${API_BASE}/auth/meta?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to initiate OAuth' }));
    throw new Error(error.message || 'Failed to initiate OAuth');
  }

  return response.json();
}

/**
 * Get all Meta connections (optionally filtered by propertyId)
 */
export async function getMetaConnections(propertyId: string, token: string): Promise<MetaConnection[]> {
  const params = `?propertyId=${encodeURIComponent(propertyId)}`;

  const response = await fetch(`${API_BASE}/api/meta-connections${params}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch connections' }));
    throw new Error(error.message || 'Failed to fetch connections');
  }

  const data = await response.json();
  return data.connections || [];
}

/**
 * Get a single Meta connection by ID
 */
async function getMetaConnection(connectionId: string, token: string): Promise<MetaConnection> {
  const response = await fetch(`${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch connection' }));
    throw new Error(error.message || 'Failed to fetch connection');
  }

  return response.json();
}

/**
 * Get connection status
 */
async function getConnectionStatus(connectionId: string, token: string): Promise<ConnectionStatus> {
  const response = await fetch(`${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}/status`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to check status' }));
    throw new Error(error.message || 'Failed to check status');
  }

  return response.json();
}

/**
 * Refresh connection token
 */
export async function refreshConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/auth/meta/refresh`, {
    method: 'POST',
    headers: authHeaders(token, 'application/json'),
    credentials: 'include',
    body: JSON.stringify({ connectionId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to refresh token' }));
    throw new Error(error.message || 'Failed to refresh token');
  }

  return response.json();
}

/**
 * Disconnect (delete) a Meta connection
 */
export async function disconnectMetaConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disconnect' }));
    throw new Error(error.message || 'Failed to disconnect');
  }

  return response.json();
}

/**
 * Get all campaigns for a Meta connection
 */
async function getMetaCampaigns(connectionId: string, token: string): Promise<{
  accounts: Array<{
    accountId: string;
    accountName: string;
    campaigns: Array<{
      id: string;
      name: string;
      status: string;
      isActive: boolean;
    }>;
  }>;
}> {
  const response = await fetch(`${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}/campaigns`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch campaigns' }));
    throw new Error(error.message || 'Failed to fetch campaigns');
  }

  return response.json();
}

/**
 * Get campaigns with metrics for a specific ad account
 * Optionally pass a date range (since/until as YYYY-MM-DD)
 */
export async function getMetaAccountCampaigns(
  connectionId: string,
  accountId: string,
  token: string,
  dateRange?: { since: string; until: string },
): Promise<{
  account: {
    id: string;
    name: string;
    currency: string;
    timezone: string;
    isActive: boolean;
  };
  campaigns: Array<{
    id: string;
    name: string;
    status: string;
    isActive: boolean;
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
  }>;
}> {
  const params = new URLSearchParams();
  if (dateRange?.since) params.set('since', dateRange.since);
  if (dateRange?.until) params.set('until', dateRange.until);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const response = await fetch(
    `${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}/accounts/${encodeURIComponent(accountId)}/campaigns${qs}`,
    {
      method: 'GET',
      headers: authHeaders(token),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch account campaigns' }));
    throw new Error(error.message || 'Failed to fetch account campaigns');
  }

  return response.json();
}

export interface DailyInsightRow {
  date: string;
  campaign_id: string;
  campaign_name: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

/**
 * Get daily campaign insights for an ad account (broken down by day)
 */
export async function getMetaDailyInsights(
  connectionId: string,
  accountId: string,
  token: string,
  since: string,
  until: string,
): Promise<{
  account: { id: string; name: string; currency: string; timezone: string };
  insights: DailyInsightRow[];
}> {
  const params = new URLSearchParams({ since, until });
  const response = await fetch(
    `${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}/accounts/${encodeURIComponent(accountId)}/daily-insights?${params.toString()}`,
    {
      method: 'GET',
      headers: authHeaders(token),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch daily insights' }));
    throw new Error(error.message || 'Failed to fetch daily insights');
  }

  return response.json();
}

/**
 * Get ads for a campaign
 */
export async function getMetaCampaignAds(connectionId: string, campaignId: string, token: string): Promise<{
  campaignId: string;
  ads: Array<{
    id: string;
    name: string;
    status: string;
    isActive: boolean;
    adsetId: string;
    creative: {
      thumbnailUrl?: string;
      imageUrl?: string;
      videoId?: string;
      callToActionType?: string;
      destinationUrl?: string;
    };
  }>;
}> {
  const response = await fetch(
    `${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}/campaigns/${encodeURIComponent(campaignId)}/ads`,
    {
      method: 'GET',
      headers: authHeaders(token),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch campaign ads' }));
    throw new Error(error.message || 'Failed to fetch campaign ads');
  }

  return response.json();
}

/**
 * Get detailed ad creative information
 */
async function getMetaAdDetails(connectionId: string, adId: string, token: string): Promise<{
  ad: {
    id: string;
    name: string;
    status: string;
    destinationUrl?: string;
    creative?: {
      name?: string;
      title?: string;
      body?: string;
      image_url?: string;
      video_id?: string;
      thumbnail_url?: string;
      call_to_action_type?: string;
      link_url?: string;
      object_story_spec?: any;
    };
  };
}> {
  const response = await fetch(
    `${API_BASE}/api/meta-connections/${encodeURIComponent(connectionId)}/ads/${encodeURIComponent(adId)}`,
    {
      method: 'GET',
      headers: authHeaders(token),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch ad details' }));
    throw new Error(error.message || 'Failed to fetch ad details');
  }

  return response.json();
}

// ============================================================================
// Google Search Console OAuth Functions
// ============================================================================

export interface GSCConnection {
  id: string;
  propertyId: string;
  userId?: string;
  googleUserId: string;
  googleUserEmail?: string;
  googleUserName?: string;
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  tokenExpiresAt?: string;
  syncState?: {
    lastSyncDate?: string;
    status: 'idle' | 'syncing' | 'error';
    errorMessage?: string;
  };
}

/**
 * Initiate Google Search Console OAuth flow
 */
export async function initiateGSCOAuth(propertyId: string, token: string, userId?: string): Promise<OAuthInitiateResponse> {
  const params = new URLSearchParams({ propertyId });
  if (userId) {
    params.append('userId', userId);
  }

  const response = await fetch(`${API_BASE}/auth/gsc?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to initiate GSC OAuth' }));
    throw new Error(error.message || 'Failed to initiate GSC OAuth');
  }

  return response.json();
}

/**
 * Get all GSC connections (optionally filtered by propertyId)
 */
export async function getGSCConnections(propertyId: string, token: string): Promise<GSCConnection[]> {
  const params = `?propertyId=${encodeURIComponent(propertyId)}`;

  const response = await fetch(`${API_BASE}/api/gsc-connections${params}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch GSC connections' }));
    throw new Error(error.message || 'Failed to fetch GSC connections');
  }

  const data = await response.json();
  return data.connections || [];
}

/**
 * Disconnect (delete) a GSC connection
 */
export async function disconnectGSCConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/gsc-connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disconnect GSC' }));
    throw new Error(error.message || 'Failed to disconnect GSC');
  }

  return response.json();
}

// ============================================================================
// Google Ads OAuth Functions
// ============================================================================

export interface GoogleAdsConnection {
  id: string;
  propertyId: string;
  userId?: string;
  googleUserId: string;
  googleUserEmail?: string;
  googleUserName?: string;
  customerAccounts?: Array<{
    id: string;
    name: string;
    currency: string;
    timezone: string;
    isActive: boolean;
    managerId?: string;
    /** A manager (MCC) account, which holds no campaign metrics itself */
    isManager?: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  tokenExpiresAt?: string;
  syncState?: {
    lastSyncDate?: string;
    status: 'idle' | 'syncing' | 'error';
    errorMessage?: string;
  };
}

export async function initiateGoogleAdsOAuth(propertyId: string, token: string, userId?: string): Promise<OAuthInitiateResponse> {
  const params = new URLSearchParams({ propertyId });
  if (userId) params.append('userId', userId);
  const response = await fetch(`${API_BASE}/auth/google-ads?${params}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to initiate OAuth' }));
    throw new Error(error.message || 'Failed to initiate OAuth');
  }
  return response.json();
}

export async function getGoogleAdsConnections(propertyId: string, token: string): Promise<GoogleAdsConnection[]> {
  const response = await fetch(`${API_BASE}/api/google-ads-connections?propertyId=${encodeURIComponent(propertyId)}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch connections' }));
    throw new Error(error.message || 'Failed to fetch connections');
  }
  const data = await response.json();
  return data.connections || [];
}

export async function disconnectGoogleAdsConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/google-ads-connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disconnect' }));
    throw new Error(error.message || 'Failed to disconnect');
  }
  return response.json();
}

export async function refreshGoogleAdsConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/auth/google-ads/refresh`, {
    method: 'POST',
    headers: authHeaders(token, 'application/json'),
    credentials: 'include',
    body: JSON.stringify({ connectionId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to refresh token' }));
    throw new Error(error.message || 'Failed to refresh token');
  }
  return response.json();
}

export async function updateGoogleAdsSelectedAccounts(connectionId: string, selectedIds: string[], token: string): Promise<{ success: boolean; customerAccounts: any[] }> {
  const response = await fetch(`${API_BASE}/api/google-ads-connections/${encodeURIComponent(connectionId)}/selected-accounts`, {
    method: 'PATCH',
    headers: authHeaders(token, 'application/json'),
    credentials: 'include',
    body: JSON.stringify({ selectedIds }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update selected accounts' }));
    throw new Error(error.message || 'Failed to update selected accounts');
  }
  return response.json();
}

export async function discoverGoogleAdsAccounts(connectionId: string, token: string): Promise<GoogleAdsConnection> {
  const response = await fetch(`${API_BASE}/api/google-ads-connections/${encodeURIComponent(connectionId)}/discover-accounts`, {
    method: 'POST',
    headers: authHeaders(token),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to discover accounts' }));
    throw new Error(error.message || 'Failed to discover accounts');
  }
  const data = await response.json();
  return data.connection;
}

export interface GoogleAdsInsightRow {
  date: string;
  campaignId: string;
  campaignName: string;
  status: string;
  spend: string;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  avgCpc: string;
}

export async function getGoogleAdsInsights(
  connectionId: string,
  customerId: string,
  startDate: string,
  endDate: string,
  token: string,
): Promise<{ rowCount: number; rows: GoogleAdsInsightRow[] }> {
  const response = await fetch(`${API_BASE}/api/google-ads-connections/${encodeURIComponent(connectionId)}/insights`, {
    method: 'POST',
    headers: authHeaders(token, 'application/json'),
    credentials: 'include',
    body: JSON.stringify({ customerId, startDate, endDate }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch insights' }));
    throw new Error(error.message || 'Failed to fetch insights');
  }
  return response.json();
}

// ============================================================================
// Google/BigQuery OAuth Functions
// ============================================================================

/**
 * Initiate Google OAuth flow
 */
export async function initiateGoogleOAuth(propertyId: string, token: string, userId?: string): Promise<OAuthInitiateResponse> {
  const params = new URLSearchParams({ propertyId });
  if (userId) {
    params.append('userId', userId);
  }

  const response = await fetch(`${API_BASE}/auth/google?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to initiate OAuth' }));
    throw new Error(error.message || 'Failed to initiate OAuth');
  }

  return response.json();
}

/**
 * Get all Google connections (optionally filtered by propertyId)
 */
export async function getGoogleConnections(propertyId: string, token: string): Promise<GoogleConnection[]> {
  const params = `?propertyId=${encodeURIComponent(propertyId)}`;

  const response = await fetch(`${API_BASE}/api/google-connections${params}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch connections' }));
    throw new Error(error.message || 'Failed to fetch connections');
  }

  const data = await response.json();
  return data.connections || [];
}

/**
 * Get a single Google connection by ID
 */
async function getGoogleConnection(connectionId: string, token: string): Promise<GoogleConnection> {
  const response = await fetch(`${API_BASE}/api/google-connections/${encodeURIComponent(connectionId)}`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch connection' }));
    throw new Error(error.message || 'Failed to fetch connection');
  }

  return response.json();
}

/**
 * Get Google connection status
 */
async function getGoogleConnectionStatus(connectionId: string, token: string): Promise<ConnectionStatus> {
  const response = await fetch(`${API_BASE}/api/google-connections/${encodeURIComponent(connectionId)}/status`, {
    method: 'GET',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to check status' }));
    throw new Error(error.message || 'Failed to check status');
  }

  return response.json();
}

/**
 * Refresh Google connection token
 */
export async function refreshGoogleConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/auth/google/refresh`, {
    method: 'POST',
    headers: authHeaders(token, 'application/json'),
    credentials: 'include',
    body: JSON.stringify({ connectionId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to refresh token' }));
    throw new Error(error.message || 'Failed to refresh token');
  }

  return response.json();
}

export interface GA4SyncRow {
  date: string;
  eventName: string;
  country: string;
  deviceCategory: string;
  activeUsers: number;
  eventCount: number;
  sessions: number;
  bounceRate: number;
  engagedSessions: number;
}

interface GA4SyncResult {
  success: boolean;
  rowCount: number;
  rows: GA4SyncRow[];
}

/**
 * Fetch GA4 data for a date range (real-time, no DB persistence)
 */
export async function syncGA4Data(
  connectionId: string,
  ga4PropertyId: string,
  startDate: string,
  endDate: string,
  token: string,
): Promise<GA4SyncResult> {
  const response = await fetch(
    `${API_BASE}/api/google-connections/${encodeURIComponent(connectionId)}/sync`,
    {
      method: 'POST',
      headers: authHeaders(token, 'application/json'),
      credentials: 'include',
      body: JSON.stringify({ ga4PropertyId, startDate, endDate }),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Sync failed' }));
    throw new Error(error.message || 'Sync failed');
  }

  return response.json();
}

/**
 * List all GA4 properties across all accounts for a connection (auto-populated)
 */
export async function getAllGA4Properties(
  connectionId: string,
  token: string,
): Promise<Array<{ id: string; fullId: string; displayName: string; accountName: string; timeZone?: string }>> {
  const response = await fetch(
    `${API_BASE}/api/google-connections/${encodeURIComponent(connectionId)}/all-properties`,
    {
      method: 'GET',
      headers: authHeaders(token),
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch properties' }));
    throw new Error(error.message || 'Failed to fetch properties');
  }

  const data = await response.json();
  return data.properties || [];
}

/**
 * Disconnect (delete) a Google connection
 */
export async function disconnectGoogleConnection(connectionId: string, token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/google-connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disconnect' }));
    throw new Error(error.message || 'Failed to disconnect');
  }

  return response.json();
}

